import type { FilterField } from '@/shared/components/filter/types'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	FieldConfig,
	FieldConfigType,
} from '@/shared/components/query-builder/types'
import type { FieldSchemaModel } from '@/shared/generated'
import type { Placement } from '@floating-ui/react-dom'

import { useMemo } from 'react'
import { omitAll } from 'lodash/fp'

import { useBiFieldsQuery, useBiFieldValuesQuery } from '@/entities/bi'

import BaseFilter from '../Filter/BaseFilter'
import { useFilterFieldsConfig } from './useFilterFiledsConfig'

type Props = {
	filters: FilterSchema[]
	onChange(filters: FilterSchema[]): void
	placement?: Placement
	fields?: FilterField[]
	irremovableCount?: number
}
const isSupportedField = (field: FilterField) =>
	!['optimization_group_id', 'pos_ids', 'category_ids'].includes(field.value)

export default function MainDashboardFilter(props: Props) {
	const biFieldsQuery = useBiFieldsQuery<FieldSchemaModel[]>()
	const { fields, dataChoices } = useFilterFieldsConfig({
		sanitizeFields: omitAll(['optimization_group_id', 'pos_ids']),
	})
	const extendedFieldConfig = useMemo(
		() =>
			biFieldsQuery.data

				?.filter((schema) => (schema.options as any).is_dynamic_values)
				.reduce((acc, schema) => {
					acc[schema.id] = {
						useSelectOptions() {
							return useBiFieldValuesQuery(schema.id, {
								select: (list) =>
									list?.map((value) => ({ value, label: value })),
							})
						},
					} as FieldConfig

					return acc
				}, {} as FieldConfigType) as FieldConfigType,
		[biFieldsQuery.data]
	)

	return (
		<BaseFilter
			{...props}
			onChange={(filters) => {
				props.onChange(
					filters.slice(props.irremovableCount || 0, filters.length)
				)
			}}
			// Exclude optimization_group (deprecated) field from the filter fields
			fields={fields.filter(isSupportedField)}
			extendedFieldConfig={extendedFieldConfig}
			options={dataChoices}
		/>
	)
}
